/**
 * Channel MCP process sweep (ENG-4453 / ENG-4448).
 *
 * Defense-in-depth: kill surplus per-agent channel MCP processes that escaped
 * the stdin-close + SIGTERM/SIGINT handlers added in ENG-4434. Slack and
 * Telegram load-balance inbound events across every connected long-poll /
 * Socket Mode waiter, so orphan channel children silently swallow ~85% of
 * traffic when they accumulate.
 *
 * The sweep runs on a throttle (default every 5 min) from the manager poll
 * loop. For each configured agent, it looks for `slack-channel.js`,
 * `direct-chat-channel.js`, `telegram-channel.js` processes whose env includes
 * `AGT_AGENT_CODE_NAME=<code_name>` (all three servers set this, starting
 * ENG-4436). Expected count per agent per channel type is 1.
 *
 * Live-process determination — three signals, in priority order:
 *   1. **PPID chain anchored on the agent's tmux pane** (authoritative). The
 *      caller passes `livePidsByAgent`, populated by `tmux list-panes`. Any
 *      MCP whose ancestor chain reaches one of those pids is live; everything
 *      else for that agent is zombie. This deterministically resolves the
 *      "two non-orphans" case that older sweep versions punted on.
 *   2. **PPID === 1** (definite orphan). Used as a fallback when the tmux
 *      anchor is unavailable (no live session, lookup failed).
 *   3. **Singleton in group**. If exactly one process exists for an agent's
 *      channel and it's not visibly orphaned, leave it — might be mid-spawn.
 *
 * Kill selection is pure — see `pickKillTargets` — so it can be unit-tested
 * without spawning processes. The ps-query, tmux query, and actual SIGTERM
 * live behind `sweepChannelProcesses`.
 */

import { execFileSync } from 'node:child_process';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ChannelType = 'slack-channel' | 'direct-chat-channel' | 'telegram-channel';

/** Basenames the MCP channel servers publish in their bin field — keep in
 *  sync with packages/mcp/package.json. */
const CHANNEL_BASENAMES: ChannelType[] = [
  'slack-channel',
  'direct-chat-channel',
  'telegram-channel',
];

export interface ChannelProcessInfo {
  pid: number;
  ppid: number;
  channelType: ChannelType;
  codeName: string;
  /** Monotonically increasing elapsed-time seconds. Higher = older. */
  etimeSeconds: number;
  /** Original ps command field, truncated. Kept for log messages. */
  command: string;
}

export interface SweepKill {
  pid: number;
  codeName: string;
  channelType: ChannelType;
  etimeSeconds: number;
  /**
   *  - `orphan`: PPID === 1 (parent died, reparented to init/launchd).
   *  - `not-in-live-chain`: PPID chain doesn't reach any of the agent's
   *    known tmux pane pids — i.e. it belongs to a previous Claude Code
   *    session that didn't clean up its children.
   */
  reason: 'orphan' | 'not-in-live-chain';
}

export interface SweepResult {
  /** Kill targets that were actually signalled (or would be in dry-run). */
  kills: SweepKill[];
  /** How many channel-matching processes the sweep inspected. */
  inspected: number;
  /** Agents the sweep considered (for log correlation). */
  scannedAgents: string[];
  dryRun: boolean;
}

// ---------------------------------------------------------------------------
// etime parsing — `[[dd-]hh:]mm:ss`
// ---------------------------------------------------------------------------

/** Convert a ps `etime` field (macOS + Linux format) to seconds. */
export function parseEtime(s: string): number {
  const trimmed = s.trim();
  if (!trimmed) return 0;

  let days = 0;
  let rest = trimmed;
  const dashIdx = rest.indexOf('-');
  if (dashIdx >= 0) {
    days = parseInt(rest.slice(0, dashIdx), 10) || 0;
    rest = rest.slice(dashIdx + 1);
  }
  const parts = rest.split(':').map((p) => parseInt(p, 10) || 0);
  // Normalise to [h, m, s] right-aligned (ps omits leading zero fields).
  let h = 0;
  let m = 0;
  let sec = 0;
  if (parts.length === 3) {
    [h, m, sec] = parts as [number, number, number];
  } else if (parts.length === 2) {
    [m, sec] = parts as [number, number];
  } else if (parts.length === 1) {
    [sec] = parts as [number];
  }
  return days * 86_400 + h * 3_600 + m * 60 + sec;
}

// ---------------------------------------------------------------------------
// ps output parsing
// ---------------------------------------------------------------------------

/**
 * Parse `ps eww -o pid=,ppid=,etime=,command=` output. `eww` appends the
 * process's env to the command field (`KEY=val KEY=val ...`), which is how
 * we scope kills to a specific agent via AGT_AGENT_CODE_NAME.
 *
 * Exported for unit tests — the live path calls this from sweepChannelProcesses.
 */
export function parsePsOutput(psOutput: string): ChannelProcessInfo[] {
  const results: ChannelProcessInfo[] = [];
  const lines = psOutput.split('\n');

  for (const rawLine of lines) {
    const line = rawLine.trimStart();
    if (!line) continue;

    // Columns: PID PPID ETIME COMMAND...
    // etime has no spaces, so splitting on whitespace for the first 3 fields
    // is safe. Everything after field 3 is the command + env.
    const firstSpace = line.search(/\s+/);
    if (firstSpace < 0) continue;
    const pid = parseInt(line.slice(0, firstSpace), 10);
    if (!Number.isFinite(pid)) continue;

    const afterPid = line.slice(firstSpace).trimStart();
    const secondSpace = afterPid.search(/\s+/);
    if (secondSpace < 0) continue;
    const ppid = parseInt(afterPid.slice(0, secondSpace), 10);
    if (!Number.isFinite(ppid)) continue;

    const afterPpid = afterPid.slice(secondSpace).trimStart();
    const thirdSpace = afterPpid.search(/\s+/);
    if (thirdSpace < 0) continue;
    const etime = afterPpid.slice(0, thirdSpace);
    const command = afterPpid.slice(thirdSpace).trimStart();

    // Match the script token at a word boundary — `ps eww` appends the whole
    // process env to the command field, so a plain `.includes()` can
    // false-positive when an env value happens to contain `slack-channel.js`
    // or similar. Path prefix excludes `=` to rule out `KEY=/path/…-channel.js`
    // env-value tokens.
    const channelMatch = command.match(
      new RegExp(`(?:^|\\s)(?:[^\\s=]*/)?(${CHANNEL_BASENAMES.join('|')})\\.js(?:\\s|$)`),
    );
    const channelType = channelMatch?.[1] as ChannelType | undefined;
    if (!channelType) continue;

    // Extract AGT_AGENT_CODE_NAME from the env-suffix on the command line.
    // Matches on a word boundary so `AGT_AGENT_CODE_NAME_FOO` can't spoof it.
    const match = command.match(/(?:^|\s)AGT_AGENT_CODE_NAME=([^\s]+)/);
    if (!match) continue;
    const codeName = match[1]!;

    results.push({
      pid,
      ppid,
      channelType,
      codeName,
      etimeSeconds: parseEtime(etime),
      command: command.slice(0, 500),
    });
  }

  return results;
}

/**
 * Parse the same `ps eww` output to build a PID → PPID map covering EVERY
 * process on the host (not just channel children). Used to walk the parent
 * chain of an MCP up to the agent's tmux pane pid.
 */
export function parseAllPids(psOutput: string): Map<number, number> {
  const map = new Map<number, number>();
  for (const rawLine of psOutput.split('\n')) {
    const line = rawLine.trimStart();
    if (!line) continue;
    const firstSpace = line.search(/\s+/);
    if (firstSpace < 0) continue;
    const pid = parseInt(line.slice(0, firstSpace), 10);
    if (!Number.isFinite(pid)) continue;
    const afterPid = line.slice(firstSpace).trimStart();
    const secondSpace = afterPid.search(/\s+/);
    if (secondSpace < 0) continue;
    const ppid = parseInt(afterPid.slice(0, secondSpace), 10);
    if (!Number.isFinite(ppid)) continue;
    map.set(pid, ppid);
  }
  return map;
}

/**
 * Walk the PPID chain from `startPid` upward until we hit pid 0/1 or visit a
 * pid we've already seen (cycle guard). Returns the set of ancestors visited
 * INCLUDING `startPid` itself.
 *
 * Bounded to 64 hops — process trees on real hosts never approach that.
 */
export function walkPpidChain(startPid: number, parentMap: Map<number, number>): Set<number> {
  const visited = new Set<number>();
  let cur = startPid;
  for (let i = 0; i < 64; i++) {
    if (cur <= 1 || visited.has(cur)) break;
    visited.add(cur);
    const parent = parentMap.get(cur);
    if (parent === undefined) break;
    cur = parent;
  }
  return visited;
}

// ---------------------------------------------------------------------------
// Kill target selection — pure
// ---------------------------------------------------------------------------

export interface PickKillTargetsArgs {
  processes: ChannelProcessInfo[];
  agentCodeNames: Set<string>;
  /** PID → PPID map for the entire host, used for chain walks. */
  parentMap: Map<number, number>;
  /** codeName → set of pids identified as live anchors (typically tmux pane
   *  pids for the agent's `agt-<codeName>` session). When a key is missing or
   *  the set is empty, the sweep falls back to PPID===1 orphan detection only
   *  for that agent. */
  livePidsByAgent: Map<string, Set<number>>;
}

export interface PickKillTargetsResult {
  kills: SweepKill[];
  /** Reserved for future use. The chain-walk eliminates the previous
   *  "ambiguous" case entirely — when livePidsByAgent has an entry for the
   *  agent, every process is decisively classified. Kept on the result type
   *  for backward compatibility with logging code. */
  ambiguousGroups: string[];
}

/**
 * Given every channel process on the host, group by (codeName, channelType)
 * and identify zombies via PPID-chain walk against the agent's known live
 * anchor pids.
 *
 * Kill rules:
 *  1. **PPID chain reaches the agent's live pid set** → live (keep).
 *  2. **PPID === 1** → orphan (kill).
 *  3. **Live anchor known but chain doesn't reach it** → not-in-live-chain
 *     (kill). This catches the previously-ambiguous case where two non-orphan
 *     processes both claim to be the agent's MCP — the one parented under the
 *     dead-but-not-yet-reaped Claude Code session gets killed.
 *  4. **Live anchor unknown** (no tmux session, lookup failed) → fall back
 *     to PPID===1 orphan detection only; never kill non-orphans without
 *     positive evidence.
 *  5. **Singleton non-orphan with no live-anchor info** → leave alone (might
 *     be mid-spawn).
 *
 * Only considers agents in `agentCodeNames` — a host shared with another
 * manager instance must not cross-kill into that instance's agents.
 */
export function pickKillTargets(args: PickKillTargetsArgs): PickKillTargetsResult {
  const { processes, agentCodeNames, parentMap, livePidsByAgent } = args;
  const kills: SweepKill[] = [];

  // Seed the parent map with each channel process's known ppid so a sparse
  // input map (or a unit-test fixture that omits these) still walks correctly.
  const fullParentMap = new Map(parentMap);
  for (const proc of processes) {
    if (!fullParentMap.has(proc.pid)) fullParentMap.set(proc.pid, proc.ppid);
  }

  const groups = new Map<string, ChannelProcessInfo[]>();
  for (const proc of processes) {
    if (!agentCodeNames.has(proc.codeName)) continue;
    const key = `${proc.codeName}:${proc.channelType}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(proc);
    else groups.set(key, [proc]);
  }

  for (const [, bucket] of groups) {
    if (bucket.length === 0) continue;
    const codeName = bucket[0]!.codeName;
    const liveAnchors = livePidsByAgent.get(codeName);
    const haveAnchors = !!liveAnchors && liveAnchors.size > 0;

    for (const proc of bucket) {
      // Definite orphan — always a kill target regardless of anchor info.
      if (proc.ppid === 1) {
        // …unless it's the only process for this group and we have no
        // anchor info: keep it as a last-resort listener (mid-respawn case).
        if (!haveAnchors && bucket.length === 1) continue;
        kills.push({
          pid: proc.pid,
          codeName: proc.codeName,
          channelType: proc.channelType,
          etimeSeconds: proc.etimeSeconds,
          reason: 'orphan',
        });
        continue;
      }

      // Non-orphan: classify via PPID-chain walk if we have anchor info.
      if (!haveAnchors) continue;

      const chain = walkPpidChain(proc.pid, fullParentMap);
      let isLive = false;
      for (const anchor of liveAnchors!) {
        if (chain.has(anchor)) { isLive = true; break; }
      }
      if (!isLive) {
        kills.push({
          pid: proc.pid,
          codeName: proc.codeName,
          channelType: proc.channelType,
          etimeSeconds: proc.etimeSeconds,
          reason: 'not-in-live-chain',
        });
      }
    }
  }

  return { kills, ambiguousGroups: [] };
}

// ---------------------------------------------------------------------------
// Live sweep — wraps ps + kill, throttled by the caller
// ---------------------------------------------------------------------------

export interface SweepOptions {
  agentCodeNames: Set<string>;
  /** When true, log kill targets but do not send SIGTERM. */
  dryRun: boolean;
  /** Injected for testability; defaults to process-level SIGTERM. */
  killFn?: (pid: number) => void;
  log: (msg: string) => void;
}

function defaultKill(pid: number): void {
  try {
    process.kill(pid, 'SIGTERM');
  } catch {
    // Process may have exited between ps and kill — race is harmless.
  }
}

/**
 * Resolve the live tmux pane pid for each agent — the authoritative anchor for
 * "this MCP belongs to the currently-running Claude Code session". Returns an
 * empty set for agents whose tmux session isn't found (no live anchor → sweep
 * falls back to orphan-only detection for that agent).
 *
 * Each agent's session name is `agt-<codeName>`; the pane pid is the program
 * running directly in that pane (Claude Code), and every MCP child has it
 * somewhere on its parent chain.
 */
export function resolveLiveAnchorPids(agentCodeNames: Iterable<string>): Map<string, Set<number>> {
  const result = new Map<string, Set<number>>();
  for (const codeName of agentCodeNames) {
    const pids = new Set<number>();
    try {
      const out = execFileSync('tmux', ['list-panes', '-t', `agt-${codeName}`, '-F', '#{pane_pid}'], {
        encoding: 'utf-8',
        timeout: 2_000,
        stdio: ['ignore', 'pipe', 'ignore'],
      });
      for (const line of out.split('\n')) {
        const pid = parseInt(line.trim(), 10);
        if (Number.isFinite(pid) && pid > 1) pids.add(pid);
      }
    } catch {
      // tmux session doesn't exist (paused/never started/crashed) — leave
      // pids empty so the sweep falls back to orphan-only detection.
    }
    result.set(codeName, pids);
  }
  return result;
}

/**
 * Run `ps eww`, classify surplus per-agent channel processes, and (unless
 * dry-run) SIGTERM them.
 *
 * The poll loop calls this; it's responsible for the throttling interval.
 */
export async function sweepChannelProcesses(opts: SweepOptions): Promise<SweepResult> {
  const { agentCodeNames, dryRun, log } = opts;
  const kill = opts.killFn ?? defaultKill;

  let psOutput = '';
  try {
    // -E on macOS, `e` on Linux — both append the process environment to the
    // command field. macOS accepts `-E`, Linux's procps-ng does not, but both
    // accept `e` as a BSD-style option when passed without the leading dash.
    psOutput = execFileSync('ps', ['eww', '-o', 'pid=,ppid=,etime=,command='], {
      encoding: 'utf-8',
      timeout: 5_000,
      maxBuffer: 10 * 1024 * 1024,
    });
  } catch (err) {
    log(`[channel-sweep] ps failed: ${(err as Error).message}`);
    return { kills: [], inspected: 0, scannedAgents: [...agentCodeNames], dryRun };
  }

  const processes = parsePsOutput(psOutput);
  const parentMap = parseAllPids(psOutput);
  const livePidsByAgent = resolveLiveAnchorPids(agentCodeNames);
  const { kills } = pickKillTargets({ processes, agentCodeNames, parentMap, livePidsByAgent });

  for (const target of kills) {
    const ageMin = Math.round(target.etimeSeconds / 60);
    log(
      `[channel-sweep]${dryRun ? '[dry-run]' : ''} surplus ${target.channelType} for ${target.codeName}: ` +
        `killing pid=${target.pid} (${target.reason}, age=${ageMin}m)`,
    );
    if (!dryRun) kill(target.pid);
  }

  return {
    kills,
    inspected: processes.length,
    scannedAgents: [...agentCodeNames],
    dryRun,
  };
}

// ---------------------------------------------------------------------------
// Targeted teardown — kill every channel MCP for one (de-provisioned) agent
// ---------------------------------------------------------------------------

/**
 * Pure: from a list of channel processes, return every pid whose
 * AGT_AGENT_CODE_NAME matches `codeName`. Exported for unit tests.
 *
 * Used by the de-provision path: when an agent is unassigned/deleted from a
 * host, the periodic `sweepChannelProcesses` will *not* clean up its slack /
 * telegram / direct-chat MCPs because that sweep is scoped to currently-known
 * agent code names. Without targeted teardown, the slack-channel and
 * telegram-channel processes survive (often reparented to init when the tmux
 * session dies), and Telegram's getUpdates load-balances inbound traffic to
 * whichever poller is alive — including the orphan on the old host. That's
 * exactly the conflict that took Scout offline after migrating from one host
 * to another.
 */
export function pickTeardownTargets(
  processes: ChannelProcessInfo[],
  codeName: string,
): ChannelProcessInfo[] {
  return processes.filter((p) => p.codeName === codeName);
}

export interface KillAgentChannelProcessesOptions {
  /** Injected for testability; defaults to process.kill SIGTERM/SIGKILL. */
  killFn?: (pid: number, signal: NodeJS.Signals) => void;
  /** Injected for testability; defaults to ps eww. */
  psFn?: () => string;
  log: (msg: string) => void;
  /** When true, log targets but do not signal them. */
  dryRun?: boolean;
}

function defaultKillSignal(pid: number, signal: NodeJS.Signals): void {
  try {
    process.kill(pid, signal);
  } catch {
    // Already exited — race is harmless.
  }
}

function defaultPs(): string {
  return execFileSync('ps', ['eww', '-o', 'pid=,ppid=,etime=,command='], {
    encoding: 'utf-8',
    timeout: 5_000,
    maxBuffer: 10 * 1024 * 1024,
  });
}

/**
 * SIGTERM every slack/telegram/direct-chat MCP for `codeName`. Best-effort —
 * failures are logged and swallowed so the de-provision path can continue.
 *
 * Returns the pids it signalled (useful for tests + log correlation).
 */
export function killAgentChannelProcesses(
  codeName: string,
  opts: KillAgentChannelProcessesOptions,
): number[] {
  const { log, dryRun = false } = opts;
  const kill = opts.killFn ?? defaultKillSignal;
  const ps = opts.psFn ?? defaultPs;

  let psOutput = '';
  try {
    psOutput = ps();
  } catch (err) {
    log(`[channel-teardown] ps failed for '${codeName}': ${(err as Error).message}`);
    return [];
  }

  const targets = pickTeardownTargets(parsePsOutput(psOutput), codeName);
  if (targets.length === 0) return [];

  const pids = targets.map((t) => t.pid);
  log(
    `[channel-teardown]${dryRun ? '[dry-run]' : ''} de-provision '${codeName}': ` +
      `killing ${targets.length} channel MCP(s) — ` +
      targets.map((t) => `${t.channelType}#${t.pid}`).join(', '),
  );

  if (!dryRun) {
    for (const pid of pids) kill(pid, 'SIGTERM');
  }
  return pids;
}
